// =============================================================================
// Design System Lapyno - Configuration centralisée des couleurs et styles
// =============================================================================

/**
 * Couleurs du Design System Lapyno
 * Utilisé par tous les composants de l'interface (Sidebars, Topbar, etc.)
 */
export const ERP_COLORS = {
  // ─────────────────────────────────────────────────────────────────────────
  // BACKGROUNDS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Background principal des sidebars */
  bgSidebar: '#F8FAFC',
  
  /** Background de la topbar */
  bgTopbar: '#FFFFFF',
  
  /** Background au hover */
  bgHover: '#E2E8F0',
  
  /** Background des éléments actifs */
  bgActive: '#EEF2FF',
  
  /** Background des inputs et zones neutres */
  bgInput: '#F8FAFC',
  
  /** Background des icônes/badges neutres */
  bgIconNeutral: '#F1F5F9',

  // ─────────────────────────────────────────────────────────────────────────
  // BORDERS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Bordure principale */
  border: '#E2E8F0',

  // ─────────────────────────────────────────────────────────────────────────
  // TEXT
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Texte principal (titres, texte actif) */
  textPrimary: '#0F172A',
  
  /** Texte secondaire (labels, descriptions) */
  textSecondary: '#64748B',
  
  /** Texte désactivé/placeholder */
  textMuted: '#94A3B8',
  
  /** Texte inactif (éléments de menu non sélectionnés) */
  textInactive: '#475569',

  // ─────────────────────────────────────────────────────────────────────────
  // ICONS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Couleur des icônes par défaut */
  iconDefault: '#64748B',
  
  /** Couleur des icônes au hover */
  iconHover: '#0F172A',

  // ─────────────────────────────────────────────────────────────────────────
  // ACCENT / MODULE COLORS
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Couleur accent principale (bleu) */
  accentPrimary: '#3B82F6',
} as const;

/**
 * Couleurs spécifiques par module (Lapyno = projects only)
 */
export const MODULE_COLORS = {
  projects: '#3B82F6',    // Blue
  admin: '#DC2626',       // Modern Red
} as const;

/**
 * Couleurs HSL par module pour les effets de glow et dégradés
 */
export const MODULE_COLORS_HSL = {
  projects: {
    primary: 'hsl(217, 91%, 60%)',
    light: 'hsl(217, 91%, 70%)',
    dark: 'hsl(217, 91%, 40%)',
    glow: 'hsla(217, 91%, 60%, 0.4)',
  },
  admin: {
    primary: 'hsl(0, 72%, 51%)',
    light: 'hsl(0, 72%, 62%)',
    dark: 'hsl(0, 72%, 40%)',
    glow: 'hsla(0, 72%, 51%, 0.4)',
  },
} as const;

/**
 * Type pour les codes de module
 */
export type ModuleColorCode = keyof typeof MODULE_COLORS;

/**
 * Obtenir la couleur d'un module
 */
export const getModuleColor = (moduleCode: ModuleColorCode): string => {
  return MODULE_COLORS[moduleCode] || ERP_COLORS.accentPrimary;
};

/**
 * Obtenir les couleurs HSL d'un module
 */
export const getModuleColorsHSL = (moduleCode: ModuleColorCode) => {
  return MODULE_COLORS_HSL[moduleCode] || MODULE_COLORS_HSL.projects;
};

/**
 * Styles de tooltip communs
 */
export const TOOLTIP_STYLES = {
  backgroundColor: '#1E293B',
  color: '#FFFFFF',
  borderColor: '#334155',
} as const;

/**
 * Styles glassmorphism
 */
export const GLASS_STYLES = {
  light: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
  },
  dark: {
    background: 'rgba(15, 23, 42, 0.7)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
} as const;
